import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { Product } from "../lib/types";

type CartState = {
  items: Record<string, { product: Product; quantity: number }>;
  bump: number;
  add: (product: Product, quantity?: number) => void;
  ensure: (product: Product) => void;
  setQuantity: (productId: string, quantity: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
};

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: {},
      bump: 0,
      add: (product, quantity = 1) =>
        set((state) => {
          const current = state.items[product.id]?.quantity ?? 0;
          const nextQuantity = Math.min(current + quantity, product.quantity_remaining || quantity);
          return { bump: state.bump + 1, items: { ...state.items, [product.id]: { product, quantity: nextQuantity } } };
        }),
      ensure: (product) =>
        set((state) => {
          if (state.items[product.id]) return state;
          const nextQuantity = Math.min(1, product.quantity_remaining || 1);
          return { bump: state.bump + 1, items: { ...state.items, [product.id]: { product, quantity: nextQuantity } } };
        }),
      setQuantity: (productId, quantity) =>
        set((state) => {
          const line = state.items[productId];
          if (!line) return state;
          if (quantity <= 0) {
            const rest = { ...state.items };
            delete rest[productId];
            return { items: rest };
          }
          return {
            items: {
              ...state.items,
              [productId]: {
                product: line.product,
                quantity: Math.min(quantity, line.product.quantity_remaining || quantity)
              }
            }
          };
        }),
      remove: (productId) =>
        set((state) => {
          const rest = { ...state.items };
          delete rest[productId];
          return { items: rest };
        }),
      clear: () => set({ items: {} })
    }),
    { name: "diecast-cart" }
  )
);

export function useCartLines() {
  return useCartStore((state) => Object.values(state.items));
}
